/**
 * Multi-service visit — pure money and Phase-1 ban predicates.
 *
 * Attendance (shift/slot/assignment) is operational; each visit service line
 * materialises its own services_delivered row for funding, contribution, and
 * claim item lineage. See docs/design/multi-service-visit-billing-proposal-2026-08-05.md §16.
 *
 * Pure module only — no Supabase I/O. SQL triggers and completion RPCs must
 * honour the same rules; unit tests share this definition.
 */

export const MULTI_SERVICE_VISIT_ERROR = {
  minutesExceedWindow: 'multi_service_minutes_exceed_window',
  minutesNotPositive: 'multi_service_minutes_not_positive',
  hourMinutesNotQuarterHour: 'multi_service_hour_minutes_not_quarter_hour',
  quantityMinutesMismatch: 'multi_service_quantity_minutes_mismatch',
  cumulativeAffordability: 'multi_service_cumulative_affordability_exceeded',
  multiClientCombo: 'multi_service_multi_client_combo_forbidden',
  cmLineForbidden: 'multi_service_cm_line_forbidden',
  mixedPayerForbidden: 'multi_service_mixed_payer_forbidden',
  duplicateSaServiceId: 'multi_service_duplicate_sa_service_id',
  claimProvenance: 'multi_service_claim_provenance_incomplete',
  nonTerminalDeliveries: 'multi_service_assignment_has_non_terminal_deliveries',
  emptyBasis: 'multi_service_apportionment_basis_required',
} as const;

export type MultiServiceVisitErrorCode =
  (typeof MULTI_SERVICE_VISIT_ERROR)[keyof typeof MULTI_SERVICE_VISIT_ERROR];

export type VisitServiceLineInput = {
  lineId: string;
  inclusionId: string;
  /** Planned / attributed whole minutes for this billable line. */
  plannedMinutes: number;
  /** SAH funding package code on the inclusion (ON/CM/AT/HM/RC/EL) or null. */
  fundingSourceCode?: string | null;
  /**
   * Delivery funding_source after materialisation, e.g. SUPPORT_AT_HOME | PRIVATE | MIXED.
   * Used for MIXED ban and claim eligibility checks.
   */
  deliveryFundingSource?: string | null;
  /**
   * Catalogue / SA service identity used for duplicate-triple detection.
   * Prefer official catalogue code when present.
   */
  saServiceId?: string | null;
  serviceCode?: string | null;
  /** Unit cost in dollars for affordability estimates. */
  unitCost?: number | null;
  /**
   * Unit type for quantity binding. Hour-like units require quarter-hour minutes.
   * Defaults to hour when omitted.
   */
  unitType?: string | null;
  apportionmentBasis?: string | null;
  lineSequence?: number | null;
};

export type OkResult = { ok: true };
export type ErrResult = { ok: false; error: MultiServiceVisitErrorCode; detail?: string };
export type GuardResult = OkResult | ErrResult;

const TERMINAL_DELIVERY_STATUSES = new Set(['COMPLETED', 'CANCELLED', 'NO_SHOW', 'INCOMPLETE']);

const HOUR_UNIT_MARKERS = new Set(['HOUR', 'HOURS', 'HR', 'HRS', 'PER_HOUR', 'HOURLY']);

function isHourUnit(unitType: string | null | undefined): boolean {
  // Unknown/null is NOT hour: multi-service VISIT/WEEK lines mint qty=1 with null
  // unit_type. Assuming hour rewrote per-visit services to minutes/60 (Claude P0-4).
  if (unitType == null || unitType.trim() === '') return false;
  const n = unitType
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  return HOUR_UNIT_MARKERS.has(n) || n.includes('HOUR');
}

/** Whole minutes; rejects non-finite and non-positive. */
export function assertPositiveWholeMinutes(minutes: number): GuardResult {
  if (!Number.isFinite(minutes) || !Number.isInteger(minutes) || minutes <= 0) {
    return { ok: false, error: MULTI_SERVICE_VISIT_ERROR.minutesNotPositive };
  }
  return { ok: true };
}

/**
 * SA hour-unit quantities must be multiples of 0.25 hours ⇒ minutes multiples of 15.
 * Non-hour units only require positive whole minutes.
 */
export function assertHourUnitQuarterHourMinutes(
  minutes: number,
  unitType?: string | null,
): GuardResult {
  const positive = assertPositiveWholeMinutes(minutes);
  if (!positive.ok) return positive;
  if (!isHourUnit(unitType)) return { ok: true };
  if (minutes % 15 !== 0) {
    return { ok: false, error: MULTI_SERVICE_VISIT_ERROR.hourMinutesNotQuarterHour };
  }
  return { ok: true };
}

/**
 * Sum of active line minutes must not exceed break-reduced attendance window.
 */
export function assertLineMinutesWithinWindow(input: {
  lines: Array<{ plannedMinutes: number }>;
  attendanceMinutes: number;
}): GuardResult {
  const window = Math.trunc(input.attendanceMinutes);
  if (!Number.isFinite(window) || window <= 0) {
    return {
      ok: false,
      error: MULTI_SERVICE_VISIT_ERROR.minutesExceedWindow,
      detail: 'invalid_window',
    };
  }
  let sum = 0;
  for (const line of input.lines) {
    const check = assertPositiveWholeMinutes(line.plannedMinutes);
    if (!check.ok) return check;
    sum += line.plannedMinutes;
  }
  if (sum > window) {
    return {
      ok: false,
      error: MULTI_SERVICE_VISIT_ERROR.minutesExceedWindow,
      detail: `sum=${sum};window=${window}`,
    };
  }
  return { ok: true };
}

/**
 * Derive claim/bill quantity from attributed minutes for hour-like units.
 * Hour units: minutes/60 (quarter-hour resolution).
 * VISIT / WEEK / item-like units: **1 unit per line** by default (not minutes),
 * unless minutesPerUnit is explicitly supplied.
 */
export function quantityFromAttributedMinutes(input: {
  attributedMinutes: number;
  unitType?: string | null;
  /** For non-hour units, minutes that make one unit. When omitted, non-hour = 1 unit/line. */
  minutesPerUnit?: number | null;
}): number {
  const minutes = Math.trunc(input.attributedMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return 0;
  if (isHourUnit(input.unitType)) {
    // Exact quarter-hour binding: minutes must already be % 15 === 0 for claim path.
    return minutes / 60;
  }
  if (
    input.minutesPerUnit != null &&
    Number.isFinite(input.minutesPerUnit) &&
    input.minutesPerUnit > 0
  ) {
    return Math.floor(minutes / input.minutesPerUnit);
  }
  // VISIT / WEEK / item: one billable unit per service line (Claude P0-6).
  return 1;
}

/**
 * Fail closed when caller-supplied quantity disagrees with derived quantity
 * outside an authorised audited override.
 */
export function assertQuantityMatchesMinutes(input: {
  attributedMinutes: number;
  quantity: number;
  unitType?: string | null;
  minutesPerUnit?: number | null;
  /** When true, quantity may differ (override recorded elsewhere). */
  auditedOverride?: boolean;
  /** Absolute tolerance for floating comparison (default 1e-9). */
  epsilon?: number;
}): GuardResult {
  if (input.auditedOverride) return { ok: true };
  const derived = quantityFromAttributedMinutes({
    attributedMinutes: input.attributedMinutes,
    unitType: input.unitType,
    minutesPerUnit: input.minutesPerUnit,
  });
  const eps = input.epsilon ?? 1e-9;
  if (!Number.isFinite(input.quantity) || Math.abs(input.quantity - derived) > eps) {
    return {
      ok: false,
      error: MULTI_SERVICE_VISIT_ERROR.quantityMinutesMismatch,
      detail: `quantity=${input.quantity};derived=${derived}`,
    };
  }
  return { ok: true };
}

/**
 * Estimate line cost in cents from unit cost (dollars) and quantity derived from minutes.
 */
export function estimateLineCostCents(input: {
  plannedMinutes: number;
  unitCost: number | null | undefined;
  unitType?: string | null;
}): number {
  const unitCost = input.unitCost;
  if (unitCost == null || !Number.isFinite(unitCost) || unitCost < 0) return 0;
  const qty = quantityFromAttributedMinutes({
    attributedMinutes: input.plannedMinutes,
    unitType: input.unitType,
  });
  // Integer-cent path: scale dollars→cents first, then multiply by 10000ths of a unit
  // (qty is hours at quarter-hour resolution, e.g. 0.25 → 2500 / 10000).
  // eslint-disable-next-line statura/no-floating-point-money -- one-shot dollars→cents scale for affordability estimate only; not a contribution/claim post path
  const unitCostCents = Math.round(unitCost * 100);
  const quantityMicros = Math.round(qty * 10_000);
  return Math.trunc((unitCostCents * quantityMicros) / 10_000);
}

/**
 * Cumulative affordability: sum of line-priced amounts must not exceed remaining
 * authorisation (cents). Each line is priced from its own minutes, not the whole window.
 */
export function assertCumulativeAffordability(input: {
  lines: Array<{
    plannedMinutes: number;
    unitCost?: number | null;
    unitType?: string | null;
    /** When false, line is excluded from funded cumulative check (e.g. pure PRIVATE). */
    countsTowardFundedAuthorisation?: boolean;
  }>;
  remainingAuthorisationCents: number;
}): GuardResult {
  const remaining = Math.trunc(input.remainingAuthorisationCents);
  if (!Number.isFinite(remaining) || remaining < 0) {
    return {
      ok: false,
      error: MULTI_SERVICE_VISIT_ERROR.cumulativeAffordability,
      detail: 'invalid_remaining',
    };
  }
  let total = 0;
  for (const line of input.lines) {
    if (line.countsTowardFundedAuthorisation === false) continue;
    total += estimateLineCostCents({
      plannedMinutes: line.plannedMinutes,
      unitCost: line.unitCost,
      unitType: line.unitType,
    });
  }
  if (total > remaining) {
    return {
      ok: false,
      error: MULTI_SERVICE_VISIT_ERROR.cumulativeAffordability,
      detail: `total=${total};remaining=${remaining}`,
    };
  }
  return { ok: true };
}

function resolveSaServiceKey(line: VisitServiceLineInput): string | null {
  const raw = line.saServiceId ?? line.serviceCode ?? null;
  if (raw == null) return null;
  const key = raw.trim().toUpperCase();
  return key.length > 0 ? key : null;
}

/**
 * Phase-1 ban set for authoring/materialisation.
 */
export function assertPhase1VisitLineBans(input: {
  lines: VisitServiceLineInput[];
  /** Distinct client count on the parent shift (slots). */
  distinctClientCountOnShift: number;
}): GuardResult {
  if (input.distinctClientCountOnShift > 1 && input.lines.length > 0) {
    return { ok: false, error: MULTI_SERVICE_VISIT_ERROR.multiClientCombo };
  }

  const saKeys = new Set<string>();
  for (const line of input.lines) {
    const code = (line.fundingSourceCode ?? '').trim().toUpperCase();
    if (code === 'CM') {
      return { ok: false, error: MULTI_SERVICE_VISIT_ERROR.cmLineForbidden };
    }
    const delivery = (line.deliveryFundingSource ?? '').trim().toUpperCase();
    if (delivery === 'MIXED') {
      return { ok: false, error: MULTI_SERVICE_VISIT_ERROR.mixedPayerForbidden };
    }
    // Genuinely multi-key payer on inclusion is treated as MIXED for Phase 1.
    // Callers may set deliveryFundingSource=MIXED; also reject when saServiceId path not set.

    const quarter = assertHourUnitQuarterHourMinutes(line.plannedMinutes, line.unitType);
    if (!quarter.ok) return quarter;

    const saKey = resolveSaServiceKey(line);
    if (saKey != null) {
      if (saKeys.has(saKey)) {
        return {
          ok: false,
          error: MULTI_SERVICE_VISIT_ERROR.duplicateSaServiceId,
          detail: saKey,
        };
      }
      saKeys.add(saKey);
    }
  }
  return { ok: true };
}

/**
 * Single-client multi-service claim provenance.
 * Fires when 2+ billable lines share one attendance (slot), even if only one client.
 */
export function assertMultiServiceClaimProvenance(input: {
  lines: Array<{
    attributedMinutes: number | null;
    apportionmentBasis: string | null;
  }>;
  attendanceMinutes: number;
}): GuardResult {
  if (input.lines.length <= 1) {
    // Single-line attendance: no multi-service provenance requirement.
    return { ok: true };
  }
  const window = Math.trunc(input.attendanceMinutes);
  if (!Number.isFinite(window) || window <= 0) {
    return {
      ok: false,
      error: MULTI_SERVICE_VISIT_ERROR.claimProvenance,
      detail: 'invalid_window',
    };
  }
  let sum = 0;
  for (const line of input.lines) {
    const minutes = line.attributedMinutes;
    if (
      minutes == null ||
      !Number.isFinite(minutes) ||
      !Number.isInteger(minutes) ||
      minutes <= 0
    ) {
      return { ok: false, error: MULTI_SERVICE_VISIT_ERROR.claimProvenance, detail: 'minutes' };
    }
    const basis = line.apportionmentBasis;
    if (basis == null || basis.trim() === '') {
      return { ok: false, error: MULTI_SERVICE_VISIT_ERROR.emptyBasis };
    }
    sum += minutes;
  }
  if (sum > window) {
    return {
      ok: false,
      error: MULTI_SERVICE_VISIT_ERROR.claimProvenance,
      detail: `sum=${sum};window=${window}`,
    };
  }
  return { ok: true };
}

export function isTerminalServiceDeliveryStatus(status: string | null | undefined): boolean {
  if (status == null || status.trim() === '') return false;
  return TERMINAL_DELIVERY_STATUSES.has(status.trim().toUpperCase());
}

/**
 * Clock-out / shift complete is blocked until every roster-materialised delivery
 * for the assignment is terminal (SAH + PRIVATE).
 */
export function assertAllAssignmentDeliveriesTerminal(
  statuses: Array<string | null | undefined>,
): GuardResult {
  if (statuses.length === 0) return { ok: true };
  for (const status of statuses) {
    if (!isTerminalServiceDeliveryStatus(status)) {
      return {
        ok: false,
        error: MULTI_SERVICE_VISIT_ERROR.nonTerminalDeliveries,
        detail: status == null ? 'missing' : String(status),
      };
    }
  }
  return { ok: true };
}

/**
 * Dual-path materialisation decision:
 * - zero active lines → legacy single-inclusion path
 * - ≥1 active lines → line-scoped path (ignore slot-level inclusion for materialisation)
 */
export function resolveMaterialisationPath(activeLineCount: number): 'legacy' | 'multi_line' {
  return activeLineCount > 0 ? 'multi_line' : 'legacy';
}

/**
 * Travel is attendance-level: only the first line (by sequence) may carry travel minutes.
 * All other lines must be zero.
 */
export function travelMinutesForLine(input: {
  lineSequence: number;
  attendanceTravelMinutes: number | null | undefined;
}): number {
  const travel =
    input.attendanceTravelMinutes != null && Number.isFinite(input.attendanceTravelMinutes)
      ? Math.max(0, Math.trunc(input.attendanceTravelMinutes))
      : 0;
  if (input.lineSequence <= 1) return travel;
  return 0;
}

/**
 * Bind completion quantity to attributed minutes (shipped complete-path entry).
 * Default: derive quantity. Caller-supplied quantity allowed only with auditedOverride.
 */
export function bindCompleteQuantity(input: {
  attributedMinutes: number;
  unitType?: string | null;
  quantity?: number | null;
  auditedOverride?: boolean;
  minutesPerUnit?: number | null;
}): { quantity: number; derived: boolean; guard: GuardResult } {
  const derived = quantityFromAttributedMinutes({
    attributedMinutes: input.attributedMinutes,
    unitType: input.unitType,
    minutesPerUnit: input.minutesPerUnit,
  });
  if (input.quantity == null || !Number.isFinite(input.quantity)) {
    return { quantity: derived, derived: true, guard: { ok: true } };
  }
  const guard = assertQuantityMatchesMinutes({
    attributedMinutes: input.attributedMinutes,
    quantity: input.quantity,
    unitType: input.unitType,
    minutesPerUnit: input.minutesPerUnit,
    auditedOverride: input.auditedOverride,
  });
  return {
    quantity: input.auditedOverride ? Number(input.quantity) : derived,
    derived: !input.auditedOverride,
    guard,
  };
}

/**
 * Authoring-time validation for a multi-service visit (combines bans, minutes, quarter-hour).
 */
export function validateVisitServiceLinesAuthoring(input: {
  lines: VisitServiceLineInput[];
  attendanceMinutes: number;
  distinctClientCountOnShift: number;
}): GuardResult {
  if (input.lines.length === 0) return { ok: true };

  const bans = assertPhase1VisitLineBans({
    lines: input.lines,
    distinctClientCountOnShift: input.distinctClientCountOnShift,
  });
  if (!bans.ok) return bans;

  const window = assertLineMinutesWithinWindow({
    lines: input.lines,
    attendanceMinutes: input.attendanceMinutes,
  });
  if (!window.ok) return window;

  for (const line of input.lines) {
    const basis = line.apportionmentBasis;
    if (basis == null || basis.trim() === '') {
      return { ok: false, error: MULTI_SERVICE_VISIT_ERROR.emptyBasis };
    }
  }

  return { ok: true };
}
